import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Coins, Crown, Info } from 'lucide-react';

import { BACKEND_URL } from '../config/api';

export function AdminCreditSettings() {
  const [settings, setSettings] = useState(null);
  const [loading, setLoading] = useState(true);
  const [updating, setUpdating] = useState(null);
  const [editValues, setEditValues] = useState({});
  const [activeTab, setActiveTab] = useState('actions');
  const [apiSettings, setApiSettings] = useState(null);
  const [stripeKey, setStripeKey] = useState('');
  const [testingStripe, setTestingStripe] = useState(false);

  useEffect(() => { fetchSettings(); fetchApiSettings(); }, []);

  const fetchSettings = async () => {
    try {
      const response = await axios.get(`${BACKEND_URL}/api/credits/admin/settings`, { withCredentials: true });
      setSettings(response.data);
    } catch { toast.error('Failed to load credit settings'); }
    finally { setLoading(false); }
  };

  const fetchApiSettings = async () => {
    try {
      const response = await axios.get(`${BACKEND_URL}/api/admin/api-settings`, { withCredentials: true });
      setApiSettings(response.data);
    } catch { console.error('Failed to load API settings'); }
  };

  const updateAction = async (action, updates) => {
    setUpdating(action);
    try {
      await axios.put(`${BACKEND_URL}/api/credits/admin/action`, { action, ...updates }, { withCredentials: true });
      toast.success(`Action '${action}' updated`);
      fetchSettings(); setEditValues({});
    } catch (error) { toast.error(error.response?.data?.detail || 'Failed to update'); }
    finally { setUpdating(null); }
  };

  const updateFeatureToggle = async (feature, enabled) => {
    setUpdating(feature);
    try {
      await axios.put(`${BACKEND_URL}/api/credits/admin/feature-toggle`, { feature, enabled }, { withCredentials: true });
      toast.success(`Feature '${feature}' ${enabled ? 'enabled' : 'disabled'}`);
      fetchSettings();
    } catch { toast.error('Failed to update feature'); }
    finally { setUpdating(null); }
  };

  const updateFounderBonus = async (updates) => {
    setUpdating('founder_bonus');
    try {
      await axios.put(`${BACKEND_URL}/api/credits/admin/founder-bonus`, updates, { withCredentials: true });
      toast.success('Founder bonus updated');
      fetchSettings(); setEditValues({});
    } catch { toast.error('Failed to update founder bonus'); }
    finally { setUpdating(null); }
  };

  const updateGlobalMultiplier = async (updates) => {
    setUpdating('multiplier');
    try {
      await axios.put(`${BACKEND_URL}/api/credits/admin/global-multiplier`, updates, { withCredentials: true });
      toast.success('Global multiplier updated');
      fetchSettings(); setEditValues({});
    } catch (error) { toast.error(error.response?.data?.detail || 'Failed to update multiplier'); }
    finally { setUpdating(null); }
  };

  const saveStripeKey = async () => {
    if (!stripeKey || !stripeKey.startsWith('sk_')) {
      toast.error("Invalid Stripe key format. Must start with 'sk_test_' or 'sk_live_'");
      return;
    }
    setUpdating('stripe');
    try {
      const response = await axios.put(`${BACKEND_URL}/api/admin/api-settings/stripe`, { stripe_api_key: stripeKey }, { withCredentials: true });
      toast.success(response.data.message);
      setStripeKey(''); fetchApiSettings();
    } catch (error) { toast.error(error.response?.data?.detail || 'Failed to save Stripe key'); }
    finally { setUpdating(null); }
  };

  const testStripeConnection = async () => {
    setTestingStripe(true);
    try {
      const response = await axios.get(`${BACKEND_URL}/api/admin/api-settings/stripe/test`, { withCredentials: true });
      if (response.data.success) toast.success(`Stripe connected! Key type: ${response.data.key_type}, ending in ...${response.data.key_last4}`);
      else toast.error(response.data.error || 'Stripe connection failed');
    } catch { toast.error('Failed to test Stripe connection'); }
    finally { setTestingStripe(false); }
  };

  const removeStripeKey = async () => {
    if (!window.confirm('Remove stored Stripe key? Will fall back to environment variable if set.')) return;
    setUpdating('stripe_remove');
    try {
      await axios.delete(`${BACKEND_URL}/api/admin/api-settings/stripe`, { withCredentials: true });
      toast.success('Stripe key removed'); fetchApiSettings();
    } catch { toast.error('Failed to remove Stripe key'); }
    finally { setUpdating(null); }
  };

  const updateTopupPackage = async (index, updates) => {
    setUpdating(`package_${index}`);
    try {
      await axios.put(`${BACKEND_URL}/api/credits/admin/topup-package`, { index, ...updates }, { withCredentials: true });
      toast.success('Package updated');
      fetchSettings(); setEditValues({});
    } catch { toast.error('Failed to update package'); }
    finally { setUpdating(null); }
  };

  const deleteTopupPackage = async (index) => {
    if (!window.confirm('Delete this package?')) return;
    setUpdating(`package_${index}`);
    try {
      await axios.delete(`${BACKEND_URL}/api/credits/admin/topup-package/${index}`, { withCredentials: true });
      toast.success('Package deleted'); fetchSettings();
    } catch { toast.error('Failed to delete package'); }
    finally { setUpdating(null); }
  };

  if (loading) return <div className="animate-pulse h-64 bg-slate-100 rounded-xl" />;
  if (!settings) return null;

  const featureLabels = {
    messaging_enabled: 'Messaging System',
    on_call_enabled: 'On-Call System',
    urgent_requests_enabled: 'Urgent Requests',
    credits_system_enabled: 'Credits System',
  };

  const Toggle = ({ enabled, onClick, disabled }) => (
    <button onClick={onClick} disabled={disabled} className={`relative w-12 h-6 rounded-full transition-colors ${enabled ? 'bg-emerald-500' : 'bg-slate-300'}`}>
      <div className={`absolute top-1 w-4 h-4 bg-white rounded-full shadow transition-transform ${enabled ? 'left-7' : 'left-1'}`} />
    </button>
  );

  const BigToggle = ({ enabled, onClick, disabled }) => (
    <button onClick={onClick} disabled={disabled} className={`relative w-14 h-7 rounded-full transition-colors ${enabled ? 'bg-emerald-500' : 'bg-slate-300'}`}>
      <div className={`absolute top-1 w-5 h-5 bg-white rounded-full shadow transition-transform ${enabled ? 'left-8' : 'left-1'}`} />
    </button>
  );

  return (
    <div className="bg-white rounded-xl border border-slate-200 overflow-hidden" data-testid="admin-credit-settings">
      <div className="bg-gradient-to-r from-amber-50 to-orange-50 p-4 border-b border-amber-200">
        <h3 className="text-lg font-semibold text-slate-900 flex items-center gap-2">
          <Coins className="h-5 w-5 text-amber-500" /> Pricing & Credit System
        </h3>
        <p className="text-sm text-slate-600 mt-1">All values are configurable. Changes take effect immediately.</p>
      </div>

      <div className="grid grid-cols-4 gap-3 p-4 bg-slate-50 border-b border-slate-200">
        {[
          ['Credits Issued', settings.stats?.total_credits_issued || 0],
          ['Credits Used', settings.stats?.total_credits_used || 0],
          ['Founding Employers', settings.stats?.founding_employers_count || 0],
          ['Total Employers', settings.stats?.total_employers || 0],
        ].map(([label, value]) => (
          <div key={label} className="text-center">
            <div className="text-xl font-bold text-slate-900">{value}</div>
            <div className="text-xs text-slate-500">{label}</div>
          </div>
        ))}
      </div>

      <div className="flex border-b border-slate-200 overflow-x-auto">
        {['actions', 'multiplier', 'features', 'packages', 'bonus', 'api'].map((tab) => (
          <button key={tab} onClick={() => setActiveTab(tab)}
            className={`flex-shrink-0 px-4 py-2.5 text-sm font-medium transition-colors ${activeTab === tab ? 'text-orange-600 border-b-2 border-orange-500 bg-orange-50' : 'text-slate-600 hover:text-slate-900 hover:bg-slate-50'}`}>
            {{ actions: 'Credit Actions', multiplier: 'Global Multiplier', features: 'Feature Toggles', packages: 'Top-Up Packages', bonus: 'Founder Bonus', api: 'API Keys' }[tab]}
          </button>
        ))}
      </div>

      <div className="p-4">
        {activeTab === 'actions' && (
          <div>
            <p className="text-sm text-slate-500 mb-4">Adjust credit cost and enable/disable each action.</p>
            <div className="grid grid-cols-12 gap-2 px-3 py-2 bg-slate-100 rounded-t-lg text-xs font-medium text-slate-600 uppercase">
              <div className="col-span-5">Action</div>
              <div className="col-span-3 text-center">Credit Cost</div>
              <div className="col-span-2 text-center">Enabled</div>
              <div className="col-span-2 text-center">Actions</div>
            </div>
            <div className="divide-y divide-slate-100 border border-slate-200 border-t-0 rounded-b-lg">
              {Object.entries(settings.actions || {}).map(([action, config]) => (
                <div key={action} className="grid grid-cols-12 gap-2 px-3 py-3 items-center hover:bg-slate-50">
                  <div className="col-span-5">
                    <div className="font-medium text-slate-800">{config.label || action}</div>
                    <div className="text-xs text-slate-500">{config.description}</div>
                  </div>
                  <div className="col-span-3 flex justify-center">
                    <input type="number" min="0" value={editValues[`cost_${action}`] !== undefined ? editValues[`cost_${action}`] : config.cost}
                      onChange={(e) => setEditValues({ ...editValues, [`cost_${action}`]: parseInt(e.target.value) || 0 })}
                      className="w-16 px-2 py-1 border border-slate-200 rounded text-center text-sm" />
                  </div>
                  <div className="col-span-2 flex justify-center">
                    <Toggle enabled={config.enabled} onClick={() => updateAction(action, { enabled: !config.enabled })} disabled={updating === action} />
                  </div>
                  <div className="col-span-2 flex justify-center">
                    {editValues[`cost_${action}`] !== undefined && editValues[`cost_${action}`] !== config.cost && (
                      <button onClick={() => updateAction(action, { cost: editValues[`cost_${action}`] })} disabled={updating === action}
                        className="px-2 py-1 bg-orange-500 text-white rounded text-xs font-medium hover:bg-orange-600 disabled:opacity-50">
                        {updating === action ? '...' : 'Save'}
                      </button>
                    )}
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {activeTab === 'features' && (
          <div>
            <p className="text-sm text-slate-500 mb-4">Enable or disable major features across the platform.</p>
            <div className="space-y-3">
              {Object.entries(settings.feature_toggles || {}).map(([feature, enabled]) => (
                <div key={feature} className="flex items-center justify-between bg-slate-50 rounded-lg p-4">
                  <div>
                    <div className="font-medium text-slate-800">{featureLabels[feature] || feature}</div>
                    <div className="text-sm text-slate-500">
                      {{ messaging_enabled: 'Allow employers to message tradespeople', on_call_enabled: 'Allow tradespeople to go On-Call', urgent_requests_enabled: 'Allow employers to send urgent requests', credits_system_enabled: 'Require credits for actions' }[feature]}
                    </div>
                  </div>
                  <BigToggle enabled={enabled} onClick={() => updateFeatureToggle(feature, !enabled)} disabled={updating === feature} />
                </div>
              ))}
            </div>
          </div>
        )}

        {activeTab === 'packages' && (
          <div>
            <p className="text-sm text-slate-500 mb-4">Configure credit packages for purchase.</p>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
              {(settings.topup_packages || []).map((pkg, index) => (
                <div key={`pkg-${pkg.credits}-${pkg.price}`} className={`border rounded-lg p-3 relative ${pkg.enabled ? 'border-slate-200 bg-white' : 'border-slate-100 bg-slate-50 opacity-60'} ${pkg.popular ? 'ring-2 ring-orange-400' : ''}`}>
                  {pkg.popular && <span className="absolute -top-2 left-1/2 -translate-x-1/2 bg-orange-500 text-white text-xs px-2 py-0.5 rounded-full">Popular</span>}
                  <div className="text-center mb-3 pt-2">
                    <input type="number" value={editValues[`pkg_credits_${index}`] !== undefined ? editValues[`pkg_credits_${index}`] : pkg.credits}
                      onChange={(e) => setEditValues({ ...editValues, [`pkg_credits_${index}`]: parseInt(e.target.value) || 0 })}
                      className="w-16 text-xl font-bold text-slate-900 text-center border-b border-dashed border-slate-300 bg-transparent" />
                    <div className="text-xs text-slate-500">credits</div>
                  </div>
                  <div className="text-center mb-3">
                    <span className="text-slate-500">£</span>
                    <input type="number" step="0.01" value={editValues[`pkg_price_${index}`] !== undefined ? editValues[`pkg_price_${index}`] : pkg.price}
                      onChange={(e) => setEditValues({ ...editValues, [`pkg_price_${index}`]: parseFloat(e.target.value) || 0 })}
                      className="w-16 font-semibold text-orange-600 text-center border-b border-dashed border-slate-300 bg-transparent" />
                  </div>
                  {pkg.savings && <div className="text-center text-xs text-emerald-600 mb-2">Save {pkg.savings}</div>}
                  <div className="flex gap-1 justify-center">
                    <button onClick={() => updateTopupPackage(index, { popular: !pkg.popular })} className={`px-2 py-1 rounded text-xs ${pkg.popular ? 'bg-orange-100 text-orange-600' : 'bg-slate-100 text-slate-500'}`}>&#9733;</button>
                    <button onClick={() => updateTopupPackage(index, { enabled: !pkg.enabled })} className={`px-2 py-1 rounded text-xs ${pkg.enabled ? 'bg-emerald-100 text-emerald-600' : 'bg-slate-100 text-slate-500'}`}>{pkg.enabled ? 'On' : 'Off'}</button>
                    <button onClick={() => deleteTopupPackage(index)} className="px-2 py-1 rounded text-xs bg-red-100 text-red-600">&times;</button>
                  </div>
                  {(editValues[`pkg_credits_${index}`] !== undefined || editValues[`pkg_price_${index}`] !== undefined) && (
                    <button onClick={() => updateTopupPackage(index, { credits: editValues[`pkg_credits_${index}`] ?? pkg.credits, price: editValues[`pkg_price_${index}`] ?? pkg.price })}
                      disabled={updating === `package_${index}`} className="w-full mt-2 px-2 py-1 bg-orange-500 text-white rounded text-xs font-medium hover:bg-orange-600">Save Changes</button>
                  )}
                </div>
              ))}
            </div>
          </div>
        )}

        {activeTab === 'bonus' && (
          <div>
            <p className="text-sm text-slate-500 mb-4">Configure the founding employer bonus.</p>
            <div className="bg-gradient-to-r from-amber-50 to-orange-50 rounded-lg p-4 border border-amber-200">
              <div className="flex items-center gap-2 text-amber-700 font-medium mb-4"><Crown className="h-5 w-5" /> Founding Employer Offer</div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-1">Bonus Percentage</label>
                  <div className="flex items-center gap-2">
                    <input type="number" min="0" max="100" value={editValues['bonus_percent'] !== undefined ? editValues['bonus_percent'] : settings.founder_bonus?.percent || 20}
                      onChange={(e) => setEditValues({ ...editValues, bonus_percent: parseInt(e.target.value) || 0 })} className="w-20 px-3 py-2 border border-slate-200 rounded-lg text-center" />
                    <span className="text-slate-600">%</span>
                  </div>
                  <p className="text-xs text-slate-500 mt-1">Extra value on all credits</p>
                </div>
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-1">Duration</label>
                  <div className="flex items-center gap-2">
                    <input type="number" min="1" value={editValues['bonus_months'] !== undefined ? editValues['bonus_months'] : settings.founder_bonus?.duration_months || 12}
                      onChange={(e) => setEditValues({ ...editValues, bonus_months: parseInt(e.target.value) || 1 })} className="w-20 px-3 py-2 border border-slate-200 rounded-lg text-center" />
                    <span className="text-slate-600">months</span>
                  </div>
                  <p className="text-xs text-slate-500 mt-1">How long bonus lasts</p>
                </div>
              </div>
              {(editValues['bonus_percent'] !== undefined || editValues['bonus_months'] !== undefined) && (
                <button onClick={() => updateFounderBonus({ percent: editValues['bonus_percent'] ?? settings.founder_bonus?.percent, duration_months: editValues['bonus_months'] ?? settings.founder_bonus?.duration_months })}
                  disabled={updating === 'founder_bonus'} className="mt-4 px-4 py-2 bg-amber-500 text-white rounded-lg font-medium hover:bg-amber-600 disabled:opacity-50">
                  {updating === 'founder_bonus' ? 'Saving...' : 'Save Bonus Settings'}
                </button>
              )}
              <div className="mt-4 pt-4 border-t border-amber-200">
                <p className="text-sm text-amber-600"><strong>{settings.stats?.founding_employers_count || 0}</strong> founding employers currently active.</p>
              </div>
            </div>
          </div>
        )}

        {activeTab === 'multiplier' && (
          <div>
            <p className="text-sm text-slate-500 mb-4">Apply a global multiplier to all credit costs. Use for sales or surge pricing.</p>
            <div className={`rounded-lg p-5 border-2 ${settings.global_multiplier?.enabled ? settings.global_multiplier?.value < 1 ? 'bg-emerald-50 border-emerald-300' : settings.global_multiplier?.value > 1 ? 'bg-red-50 border-red-300' : 'bg-slate-50 border-slate-200' : 'bg-slate-50 border-slate-200'}`}>
              <div className="flex items-center justify-between mb-5">
                <div className="flex items-center gap-3">
                  <div className={`w-12 h-12 rounded-full flex items-center justify-center text-xl font-bold ${settings.global_multiplier?.enabled ? settings.global_multiplier?.value < 1 ? 'bg-emerald-500 text-white' : settings.global_multiplier?.value > 1 ? 'bg-red-500 text-white' : 'bg-slate-300 text-slate-700' : 'bg-slate-200 text-slate-500'}`}>
                    {settings.global_multiplier?.value || 1}x
                  </div>
                  <div>
                    <div className="font-semibold text-slate-800">{settings.global_multiplier?.label || 'Normal Pricing'}</div>
                    <div className="text-sm text-slate-500">{settings.global_multiplier?.enabled ? settings.global_multiplier?.value < 1 ? `${Math.round((1 - settings.global_multiplier.value) * 100)}% discount active` : settings.global_multiplier?.value > 1 ? `${Math.round((settings.global_multiplier.value - 1) * 100)}% surge pricing` : 'Standard rates' : 'Multiplier disabled'}</div>
                  </div>
                </div>
                <BigToggle enabled={settings.global_multiplier?.enabled} onClick={() => updateGlobalMultiplier({ enabled: !settings.global_multiplier?.enabled })} disabled={updating === 'multiplier'} />
              </div>
              <div className="mb-5">
                <label className="block text-sm font-medium text-slate-700 mb-2">Multiplier Value</label>
                <div className="flex items-center gap-4">
                  <input type="range" min="0.1" max="3" step="0.1" value={editValues['multiplier_value'] !== undefined ? editValues['multiplier_value'] : (settings.global_multiplier?.value || 1)}
                    onChange={(e) => setEditValues({ ...editValues, multiplier_value: parseFloat(e.target.value) })} className="flex-1 h-2 bg-slate-200 rounded-lg appearance-none cursor-pointer accent-orange-500" />
                  <input type="number" step="0.1" min="0.1" max="5" value={editValues['multiplier_value'] !== undefined ? editValues['multiplier_value'] : (settings.global_multiplier?.value || 1)}
                    onChange={(e) => setEditValues({ ...editValues, multiplier_value: parseFloat(e.target.value) || 1 })} className="w-20 px-3 py-2 border border-slate-300 rounded-lg text-center font-medium" />
                </div>
                <div className="flex justify-between text-xs text-slate-500 mt-1"><span>90% off (0.1x)</span><span>Normal (1x)</span><span>3x surge</span></div>
              </div>
              <div className="grid grid-cols-2 gap-4 mb-4">
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-1">Display Label</label>
                  <input type="text" placeholder="e.g., 50% OFF Summer Sale!" value={editValues['multiplier_label'] !== undefined ? editValues['multiplier_label'] : (settings.global_multiplier?.label || '')}
                    onChange={(e) => setEditValues({ ...editValues, multiplier_label: e.target.value })} className="w-full px-3 py-2 border border-slate-300 rounded-lg" />
                </div>
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-1">Description (optional)</label>
                  <input type="text" placeholder="e.g., Limited time offer!" value={editValues['multiplier_description'] !== undefined ? editValues['multiplier_description'] : (settings.global_multiplier?.description || '')}
                    onChange={(e) => setEditValues({ ...editValues, multiplier_description: e.target.value })} className="w-full px-3 py-2 border border-slate-300 rounded-lg" />
                </div>
              </div>
              <div className="mb-4">
                <label className="block text-sm font-medium text-slate-700 mb-2">Quick Presets</label>
                <div className="flex flex-wrap gap-2">
                  {[{ value: 0.5, label: '50% OFF', desc: 'Half Price Sale' }, { value: 0.75, label: '25% OFF', desc: 'Quarter Off' }, { value: 1.0, label: 'Normal', desc: 'Standard Pricing' }, { value: 1.25, label: '+25%', desc: 'Moderate Surge' }, { value: 1.5, label: '+50%', desc: 'High Demand' }, { value: 2.0, label: '2x', desc: 'Peak Surge' }].map((preset) => (
                    <button key={preset.value} onClick={() => setEditValues({ ...editValues, multiplier_value: preset.value, multiplier_label: preset.label, multiplier_description: preset.desc })}
                      className={`px-3 py-1.5 rounded-lg text-sm font-medium border transition-colors ${(editValues['multiplier_value'] || settings.global_multiplier?.value) === preset.value ? 'bg-orange-500 text-white border-orange-500' : preset.value < 1 ? 'bg-emerald-50 text-emerald-700 border-emerald-200 hover:bg-emerald-100' : preset.value > 1 ? 'bg-red-50 text-red-700 border-red-200 hover:bg-red-100' : 'bg-slate-50 text-slate-700 border-slate-200 hover:bg-slate-100'}`}>
                      {preset.label}
                    </button>
                  ))}
                </div>
              </div>
              {(editValues['multiplier_value'] !== undefined || editValues['multiplier_label'] !== undefined || editValues['multiplier_description'] !== undefined) && (
                <button onClick={() => updateGlobalMultiplier({ value: editValues['multiplier_value'] ?? settings.global_multiplier?.value, label: editValues['multiplier_label'] ?? settings.global_multiplier?.label, description: editValues['multiplier_description'] ?? settings.global_multiplier?.description })}
                  disabled={updating === 'multiplier'} className="px-5 py-2.5 bg-orange-500 text-white rounded-lg font-medium hover:bg-orange-600 disabled:opacity-50">
                  {updating === 'multiplier' ? 'Saving...' : 'Apply Multiplier Changes'}
                </button>
              )}
            </div>
            <div className="mt-5 bg-slate-100 rounded-lg p-4">
              <h4 className="font-medium text-slate-700 mb-3">Preview: How Costs Will Change</h4>
              <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
                {Object.entries(settings.actions || {}).slice(0, 6).map(([action, config]) => {
                  const mv = editValues['multiplier_value'] ?? settings.global_multiplier?.value ?? 1;
                  const newCost = Math.round(config.cost * mv);
                  const hasChange = mv !== 1;
                  return (
                    <div key={action} className="bg-white rounded-lg p-3 border border-slate-200">
                      <div className="text-xs text-slate-500 mb-1">{config.label || action}</div>
                      <div className="flex items-center gap-2">
                        {hasChange && <span className="text-slate-400 line-through text-sm">{config.cost}</span>}
                        <span className={`font-bold ${hasChange && mv < 1 ? 'text-emerald-600' : hasChange && mv > 1 ? 'text-red-600' : 'text-slate-700'}`}>{newCost} credits</span>
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          </div>
        )}

        {activeTab === 'api' && (
          <div>
            <p className="text-sm text-slate-500 mb-4">Configure API keys for payment processing.</p>
            <div className="border rounded-lg overflow-hidden mb-4">
              <div className="bg-slate-50 px-4 py-3 border-b flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <div className="w-8 h-8 bg-indigo-100 rounded flex items-center justify-center">
                    <svg viewBox="0 0 24 24" className="w-5 h-5 text-indigo-600" fill="currentColor"><path d="M13.976 9.15c-2.172-.806-3.356-1.426-3.356-2.409 0-.831.683-1.305 1.901-1.305 2.227 0 4.515.858 6.09 1.631l.89-5.494C18.252.975 15.697 0 12.165 0 9.667 0 7.589.654 6.104 1.872 4.56 3.147 3.757 4.992 3.757 7.218c0 4.039 2.467 5.76 6.476 7.219 2.585.92 3.445 1.574 3.445 2.583 0 .98-.84 1.545-2.354 1.545-1.875 0-4.965-.921-6.99-2.109l-.9 5.555C5.175 22.99 8.385 24 11.714 24c2.641 0 4.843-.624 6.328-1.813 1.664-1.305 2.525-3.236 2.525-5.732 0-4.128-2.524-5.851-6.594-7.305h.003z"/></svg>
                  </div>
                  <div>
                    <div className="font-medium text-slate-800">Stripe Payment Gateway</div>
                    <div className="text-xs text-slate-500">Process credit purchases and subscriptions</div>
                  </div>
                </div>
                <div className={`px-2 py-1 rounded-full text-xs font-medium ${apiSettings?.stripe_configured ? 'bg-emerald-100 text-emerald-700' : 'bg-amber-100 text-amber-700'}`}>
                  {apiSettings?.stripe_configured ? 'Connected' : 'Not Configured'}
                </div>
              </div>
              <div className="p-4">
                {apiSettings?.stripe_configured ? (
                  <div className="space-y-3">
                    <div className="flex items-center justify-between p-3 bg-slate-50 rounded-lg">
                      <div>
                        <div className="text-sm font-medium text-slate-700">API Key ending in ...{apiSettings.stripe_key_last4}</div>
                        <div className="text-xs text-slate-500">Source: {apiSettings.stripe_key_source || 'database'} {apiSettings.updated_at && ` \u2022 Updated ${new Date(apiSettings.updated_at).toLocaleDateString()}`}</div>
                      </div>
                      <div className="flex gap-2">
                        <button onClick={testStripeConnection} disabled={testingStripe} className="px-3 py-1.5 bg-indigo-100 text-indigo-700 rounded text-sm font-medium hover:bg-indigo-200 disabled:opacity-50">{testingStripe ? 'Testing...' : 'Test Connection'}</button>
                        <button onClick={removeStripeKey} disabled={updating === 'stripe_remove'} className="px-3 py-1.5 bg-red-100 text-red-700 rounded text-sm font-medium hover:bg-red-200 disabled:opacity-50">Remove</button>
                      </div>
                    </div>
                    <div className="border-t pt-3">
                      <div className="text-sm text-slate-600 mb-2">Update API Key:</div>
                      <div className="flex gap-2">
                        <input type="password" placeholder="sk_test_... or sk_live_..." value={stripeKey} onChange={(e) => setStripeKey(e.target.value)} className="flex-1 px-3 py-2 border border-slate-300 rounded-lg text-sm" />
                        <button onClick={saveStripeKey} disabled={!stripeKey || updating === 'stripe'} className="px-4 py-2 bg-indigo-600 text-white rounded-lg text-sm font-medium hover:bg-indigo-700 disabled:opacity-50">{updating === 'stripe' ? 'Saving...' : 'Update Key'}</button>
                      </div>
                    </div>
                  </div>
                ) : (
                  <div className="space-y-3">
                    <div className="bg-amber-50 border border-amber-200 rounded-lg p-3">
                      <div className="text-sm text-amber-800"><strong>Stripe is not configured.</strong> Payment features unavailable.</div>
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-slate-700 mb-1">Stripe API Key</label>
                      <div className="flex gap-2">
                        <input type="password" placeholder="sk_test_... or sk_live_..." value={stripeKey} onChange={(e) => setStripeKey(e.target.value)} className="flex-1 px-3 py-2 border border-slate-300 rounded-lg" />
                        <button onClick={saveStripeKey} disabled={!stripeKey || updating === 'stripe'} className="px-4 py-2 bg-indigo-600 text-white rounded-lg font-medium hover:bg-indigo-700 disabled:opacity-50">{updating === 'stripe' ? 'Saving...' : 'Save Key'}</button>
                      </div>
                      <p className="text-xs text-slate-500 mt-1">Get your key from <a href="https://dashboard.stripe.com/apikeys" target="_blank" rel="noopener noreferrer" className="text-indigo-600 hover:underline">Stripe Dashboard</a></p>
                    </div>
                  </div>
                )}
              </div>
            </div>
            <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
              <div className="flex gap-3">
                <Info className="w-5 h-5 text-blue-600 flex-shrink-0 mt-0.5" />
                <div className="text-sm text-blue-800"><strong>Security Note:</strong> API keys are stored securely. Only the last 4 characters are displayed. Keys here override environment variables.</div>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

export default AdminCreditSettings;
